'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { LoggingManager, StockSymbol } from '@benzinga/session';
import { SessionContext, SessionContextType } from '@benzinga/session-context';
import { SafeType, SafePromise } from '@benzinga/safe-await';

import { InputNumber, Select } from 'antd';
import type { InputNumberRef } from 'rc-input-number';
import { CloseOutlined } from '@ant-design/icons';
import { Crossing, PriceAlerts, PriceAlertsManager } from '@benzinga/price-alert-manager';
import { useQuoteSubscription } from '@benzinga/quotes-manager-hooks';
import { DateTime } from 'luxon';
import { NBSP, PerformanceColor } from '../Ticker';
import { isString } from 'lodash';
import { filterAndSortAlertsBySymbol } from './utils';
import Hooks from '@benzinga/hooks';
import { Edit, Up, AlarmSmall, CheckmarkAlert, Delete } from '@benzinga/themed-icons';
import { formatNumberWithBigInt } from '@benzinga/utils';

export interface PriceAlertDialogProps {
  allowEdit: boolean;
  onClose: () => void;
  symbol: StockSymbol;
}

type PriceAlertChange =
  | { type: 'edit'; alert: Partial<PriceAlerts.Alert>; price: number; crossing: number }
  | { type: 'delete'; alert: Partial<PriceAlerts.Alert>; price: number }
  | { type: 'new'; symbol: StockSymbol; price: number; crossing: number };

const getCrossingValue = (crossing: 'UP' | 'DOWN' | number | undefined | null): number =>
  isString(crossing) ? (crossing === 'UP' ? Crossing.UP : Crossing.DOWN) : ((crossing as number) ?? Crossing.UP);

// New component for adding a price alert to isolate state changes
const AddPriceAlertForm = React.memo<{
  onAddAlert: (price: number, crossing: number) => void;
}>(({ onAddAlert }) => {
  const priceInputRef = React.useRef<InputNumberRef>(null);
  const [price, setPrice] = React.useState<number | undefined | null>(undefined);
  const [crossing, setCrossing] = React.useState<number>(Crossing.UP);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    priceInputRef.current?.focus();
  }, []);

  const handlePriceChange = React.useCallback((value: number | null) => {
    if (value !== null && value > 0) {
      setPrice(value);
      setError(null);
    } else {
      setPrice(null);
      setError('Price must be greater than zero');
    }
  }, []);

  const onChangeCrossing = (value: number) => {
    setCrossing(value);
  };

  const handleNewAlert = async () => {
    if (price && price > 0) {
      onAddAlert(price, crossing);
      setPrice(undefined);
      setError(null);
      priceInputRef.current?.focus();
    } else {
      setError('Price Alert cannot be empty');
    }
  };

  return (
    <AlertsAdd className="AlertsAdd">
      <AlertAddBorder className="AlertAddBorder">
        <div style={{ marginBottom: '3px', marginLeft: '3px' }}>Alert me when</div>
        <RowContainer className="RowContainer">
          <StyledInputNumber
            onChange={handlePriceChange}
            placeholder={'Price'}
            ref={priceInputRef}
            status={error ? 'error' : ''}
            style={{ height: '32px', width: '150px' }}
            value={price}
          />
          <Select
            getPopupContainer={node => node.parentNode}
            onChange={value => onChangeCrossing(value)}
            options={[
              { label: 'Above or equal to (>=)', value: Crossing.UP },
              { label: 'Below or equal to (<=)', value: Crossing.DOWN },
            ]}
            style={{ height: '32px' }}
            value={crossing}
          />
          <AddButtonContainer>
            <div className="AddButton" onClick={handleNewAlert}>
              Add
            </div>
          </AddButtonContainer>
        </RowContainer>
        {error && <div style={{ color: 'red', fontSize: '12px' }}>{error}</div>}
      </AlertAddBorder>
    </AlertsAdd>
  );
});
AddPriceAlertForm.displayName = 'AddPriceAlertForm';

const PriceAlertHeader = React.memo<PriceAlertDialogProps>(({ allowEdit, onClose, symbol }) => {
  const { quote } = useQuoteSubscription(symbol);
  const lastPrice = quote?.lastTradePrice ? formatNumberWithBigInt(quote.lastTradePrice, 2) : '\xa0';

  return (
    <div className="AlertsHeader">
      <RowContainer className="RowContainer">
        <div>
          Price Alert for {symbol} - {lastPrice}
          {NBSP}
          {quote && (
            <>
              <PerformanceColor hasArrow stat={quote.change} /> {NBSP}
              <PerformanceColor isPercentage stat={quote.percentChange} />
            </>
          )}
        </div>
        {allowEdit && (
          <PointerCursor>
            <CloseOutlined onClick={onClose} title="Close" />
          </PointerCursor>
        )}
      </RowContainer>
    </div>
  );
});
PriceAlertHeader.displayName = 'PriceAlertHeader';

export const PriceAlertDialog = React.memo<PriceAlertDialogProps>(props => {
  const session = React.useContext(SessionContext);
  const priceAlertManager = session.getManager(PriceAlertsManager);

  const initialAlerts = React.useMemo(
    () => filterAndSortAlertsBySymbol(priceAlertManager.getStoredPriceAlerts() ?? [], props.symbol),
    [priceAlertManager, props.symbol],
  );

  const [alerts, setAlerts] = React.useState<Partial<PriceAlerts.Alert>[]>(initialAlerts);

  // Refresh alerts when component mounts or symbol changes
  React.useEffect(() => {
    const refreshAlerts = async () => {
      const freshAlerts = await priceAlertManager.getPriceAlerts(true);
      if (freshAlerts.ok) {
        setAlerts(filterAndSortAlertsBySymbol(freshAlerts.ok, props.symbol));
      }
    };
    refreshAlerts();
  }, [priceAlertManager, props.symbol]);

  // Subscribe to price alert updates and will keep alerts in sync
  Hooks.useSubscriber(priceAlertManager, event => {
    if (event.type === 'price_alerts_updated') {
      setAlerts(filterAndSortAlertsBySymbol(event.categoriesAlerts, props.symbol));
    }
  });

  const updatePriceAlert = React.useCallback(
    async (change?: PriceAlertChange): SafePromise<Partial<PriceAlerts.PostAlertResponse>> => {
      let priceAlertResponse: SafeType<Partial<PriceAlerts.PostAlertResponse> | undefined> = { ok: undefined };
      if (change) {
        const logger = session.getManager(LoggingManager);
        const logMessage = (level: 'log' | 'error' | 'loading', message: string, key) =>
          logger.log(level, { category: 'Price Alert', key, message }, ['toast']);
        switch (change.type) {
          case 'delete': {
            logMessage('loading', `Deleting price alert for: ${props.symbol}`, change.alert.uuid);
            const response = await priceAlertManager.deletePriceAlert(change.alert.uuid ?? '');
            response.ok
              ? logMessage('log', `Price Alert deleted for: ${props.symbol}`, change.alert.uuid)
              : logMessage('error', `Price Alert Delete error for: ${props.symbol}`, change.alert.uuid);
            break;
          }
          case 'edit':
            logMessage('loading', `Updating price alert for ${props.symbol}`, change.alert.uuid);
            priceAlertResponse = await priceAlertManager.updatePriceAlert({
              alert: {
                priceAlert: {
                  crossing: change.crossing,
                  price: Number(change.price),
                },
                symbol: props.symbol,
                uuid: change.alert.uuid,
              },
            });
            priceAlertResponse.ok
              ? logMessage('log', `Updated price alert for ${props.symbol}`, change.alert.uuid)
              : logMessage('error', `Failed to update price alert for ${props.symbol}`, change.alert.uuid);
            break;
          case 'new':
            logMessage('loading', `Creating new price alert for ${props.symbol}`, `${change.symbol}-${change.price}`);
            priceAlertResponse = await priceAlertManager.addPriceAlert({
              alert: {
                priceAlert: {
                  crossing: change.crossing,
                  price: Number(change.price),
                },
                symbol: props.symbol,
              },
            });
            priceAlertResponse.ok
              ? logMessage('log', `Price alert created for ${props.symbol}`, `${change.symbol}-${change.price}`)
              : logMessage(
                  'error',
                  `Failed to create price alert for ${props.symbol}`,
                  `${change.symbol}-${change.price}`,
                );
            break;
        }
      }
      // Manager now refreshes cache after successful operations, so local refresh is not needed.
      return priceAlertResponse as SafeType<Partial<PriceAlerts.PostAlertResponse>>;
    },
    [priceAlertManager, props.symbol, session],
  );

  const handleAddAlert = React.useCallback(
    (price: number, crossing: number) => {
      updatePriceAlert({
        crossing,
        price,
        symbol: props.symbol,
        type: 'new',
      });
    },
    [props.symbol, updatePriceAlert],
  );

  return (
    <AlertsView className="AlertsView">
      <ColumnContainer className="ColumnContainer">
        <PriceAlertHeader allowEdit={props.allowEdit} onClose={props.onClose} symbol={props.symbol} />
        {props.allowEdit && <AddPriceAlertForm onAddAlert={handleAddAlert} />}
        <AlertBody $isEdit={props.allowEdit} className="AlertBody">
          {alerts.map(alert => (
            <React.Fragment key={alert.uuid}>
              <PriceAlertComponent
                alert={alert}
                allowEdit={props.allowEdit}
                onChange={updatePriceAlert}
                session={session}
              />
              <Line />
            </React.Fragment>
          ))}
        </AlertBody>
      </ColumnContainer>
    </AlertsView>
  );
});
PriceAlertDialog.displayName = 'PriceAlertDialog';

interface PriceAlertProps {
  allowEdit: boolean;
  alert: Partial<PriceAlerts.Alert>;
  onChange: (alert: PriceAlertChange) => void;
  session: SessionContextType;
}

const PriceAlertComponent: React.FC<PriceAlertProps> = ({ alert, allowEdit, onChange }) => {
  const priceInputRef = React.useRef<InputNumberRef>(null);
  const ogPrice = React.useRef(alert.priceAlert?.price ?? 0);
  const [price, setPrice] = React.useState<number | null>(alert.priceAlert?.price ?? 0);
  const [crossing, setCrossing] = React.useState<number>(getCrossingValue(alert.priceAlert?.crossing));
  const [editingMode, setEditingMode] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const dateCreated = DateTime.fromISO(alert.createdDate ?? '').toFormat('ff');
  const dateTriggered = DateTime.fromISO(alert.triggeredDate ?? '').toFormat('ff');
  const priceText = alert.priceAlert?.price;

  React.useEffect(() => {
    setPrice(priceText ?? 0);
    ogPrice.current = priceText ?? 0;
  }, [priceText]);

  React.useEffect(() => {
    setCrossing(getCrossingValue(alert.priceAlert?.crossing));
  }, [alert.priceAlert?.crossing]);

  React.useEffect(() => {
    if (editingMode) {
      priceInputRef.current?.focus();
      setError(null);
    }
  }, [editingMode]);

  const handlePriceChange = React.useCallback((val: number | null) => {
    if (val !== null && val > 0) {
      setPrice(val);
      setError(null);
    } else if (val !== null && val <= 0) {
      setPrice(-val);
      setError('Price must be greater than zero');
    }
  }, []);

  const onUpdate = React.useCallback(() => {
    if (price && price > 0) {
      ogPrice.current = price;
      setEditingMode(false);
      onChange({ alert, crossing, price, type: 'edit' });
    }
  }, [price, onChange, alert, crossing]);

  const onCancel = React.useCallback(() => {
    setPrice(ogPrice.current);
    setEditingMode(false);
  }, []);

  const onDelete = React.useCallback(() => {
    if (price !== null) {
      onChange({ alert, price, type: 'delete' });
    }
  }, [alert, onChange, price]);

  const onChangeCrossing = React.useCallback((value: number) => {
    setCrossing(value);
  }, []);

  const defaultVal = crossing === Crossing.UP ? Crossing.UP : Crossing.DOWN;
  if (allowEdit && editingMode) {
    return (
      <AlertWithErrorContainer>
        <AlertColumnEditContainer className="AlertColumnEditContainer">
          <StyledInputNumber
            onChange={handlePriceChange}
            ref={priceInputRef}
            status={error ? 'error' : ''}
            style={{ height: '32px', width: '30%' }}
            value={price ?? undefined}
          />
          <Select
            defaultValue={defaultVal}
            onChange={onChangeCrossing}
            options={[
              { label: 'Above or equal to (>=)', value: Crossing.UP },
              { label: 'Below or equal to (<=)', value: Crossing.DOWN },
            ]}
            style={{ height: '32px' }}
          />
          <div className="RowEditEndContainer">
            <div className="ActionButton" onClick={onUpdate}>
              Update
            </div>
            <div className="ActionButton" onClick={onCancel}>
              Cancel
            </div>
          </div>
        </AlertColumnEditContainer>
        {error && <div style={{ color: 'red', fontSize: '12px' }}>{error}</div>}
      </AlertWithErrorContainer>
    );
  } else {
    const isDown = crossing === Crossing.DOWN;

    const isTriggered = alert.status && alert.status.toString() !== 'ACTIVE' ? true : false;

    return (
      <AlertColumnContainer className="AlertColumnContainer">
        <AlertLeftOuterDiv $isTriggered={isTriggered}>
          <span>{alert.symbol}</span>
          <div className="TriggerStatus">
            {isTriggered ? <TriggeredIcon /> : <ActiveAlertIcon />}
            <AlertLeftStatusDiv $isTriggered={isTriggered}>{alert.status}</AlertLeftStatusDiv>
          </div>
          {isTriggered && <AlertDateTime>on {dateTriggered}</AlertDateTime>}
        </AlertLeftOuterDiv>
        <AlertRightOuterDiv>
          <div className="AlertDiv">
            <AlertText $isDown={isDown}>{price ?? ''}</AlertText>
            {isDown ? <DownArrow /> : <UpArrow />}
          </div>
          <span>{isDown ? `Price below or equal to (<=)` : `Price above or equal to (>=)`}</span>
          <AlertDateTime>Created {dateCreated}</AlertDateTime>
        </AlertRightOuterDiv>

        {allowEdit && (
          <RowContainer>
            <AlertIcons>
              {!isTriggered && (
                <div className="MarginedIcon">
                  <Edit onClick={() => setEditingMode(true)} title="Edit" />
                </div>
              )}
              <div className="MarginedIcon">
                <Delete onClick={onDelete} title="Delete" />
              </div>
            </AlertIcons>
          </RowContainer>
        )}
      </AlertColumnContainer>
    );
  }
};

interface AlertProps {
  $isTriggered: boolean;
}
interface AllowEditProps {
  $isEdit: boolean;
}

const AlertLeftOuterDiv = styled.div<AlertProps>`
  width: ${props => (props.$isTriggered ? '35%' : '40%')};
  display: flex;
  flex-direction: column;
  gap: 5px;
  .TriggerStatus {
    display: flex;
    align-items: center;
  }
`;

const AlertRightOuterDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  .AlertDiv {
    display: flex;
    align-items: center;
  }
`;

const AlertLeftStatusDiv = styled.div<AlertProps>`
  color: ${props => (props.$isTriggered ? 'gray' : props.theme.colors.statistic.positive)};
  margin-left: 3px;
`;

const Arrow = styled(Up)`
  font-size: 12px;
  margin-right: 0.4em;
  margin-bottom: -1px;
`;

const UpArrow = styled(Arrow)`
  fill: ${props => props.theme.colors.statistic.positive};
`;

const AlertIcon = styled(AlarmSmall)`
  width: 1.5em;
  height: 1.5em;
`;

const TriggeredIcon = styled(CheckmarkAlert)`
  width: 1.5em;
  height: 1.5em;
  fill: ${props => props.theme.colors.statistic.positive};
`;

const ActiveAlertIcon = styled(AlertIcon)`
  fill: ${props => props.theme.colors.statistic.positive};
`;

const DownArrow = styled(Arrow)`
  transform: rotate(180deg);
  fill: ${props => props.theme.colors.statistic.negative};
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  .AlertsHeader {
    background-color: ${props => props.theme.colors.backgroundActive};
    padding: 10px;
  }
`;

const AlertDateTime = styled.div`
  font-weight: bold;
  font-size: 10px;
`;

const AlertText = styled.div<{ $isDown?: boolean }>`
  color: ${({ $isDown, theme }) => ($isDown ? theme.colors.statistic.negative : theme.colors.statistic.positive)};
  margin-right: 4px;
`;

const AlertColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AlertWithErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 7px 0px;
`;

const AlertColumnEditContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 2px;

  .RowEditEndContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 3px;

    .ActionButton {
      background-color: ${props => props.theme.colors.brandMuted};
      color: ${props => props.theme.colors.foregroundActive};
      cursor: pointer;
      padding: 6px;
      :hover {
        background-color: ${props => props.theme.colors.brand};
      }
    }
  }
`;

const Line = styled.hr`
  margin: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const AlertBody = styled.div<AllowEditProps>`
  background-color: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
  max-height: 500px;
  overflow: auto;
  padding: 5px 10px 0px 10px;

  scrollbar-color: ${props => props.theme.colors.foreground} ${props => props.theme.colors.backgroundActive};

  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 6px;
  }
`;

const AlertsAdd = styled.div`
  background-color: ${props => props.theme.colors.background};
  margin-top: 2px;
`;

const AlertAddBorder = styled.div`
  border-bottom-width: 1px;
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  margin-bottom: 5px;
  padding: 5px 10px;
`;

const AlertIcons = styled.div`
  display: flex;
  flex-direction: row;
  margin: 4px;
  .MarginedIcon {
    cursor: pointer;
    margin-left: 4px;
    margin-right: 4px;
  }
`;

const AlertsView = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  border-width: 2px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  .AddButton {
    background-color: ${props => props.theme.colors.brandMuted};
    color: ${props => props.theme.colors.foregroundActive};
    cursor: pointer;
    margin-left: 2px;
    padding: 6px 10px 6px 10px;
    :hover {
      background-color: ${props => props.theme.colors.brand};
    }
  }
`;

const PointerCursor = styled.div`
  cursor: pointer;
`;

const StyledInputNumber = styled(InputNumber<number>)`
  .ant-input-number-handler-up-inner,
  .ant-input-number-handler-down-inner {
    color: ${props => props.theme.colors.foreground} !important;
  }
`;
